/**
 * UserMenu — dashboard header dropdown.
 *
 * Replicates the Lexflow header pattern (custom button + absolute
 * dropdown anchored to it) using Taxor design tokens. Pulls profile
 * + active org from /auth/me via useCurrentUser.
 *
 *   ┌───────────────────────────────────────┐
 *   │ JM  Juan Alvaro Montoya               │  ← trigger
 *   │     OWNER · SERVILINK COLOMBIA        │
 *   └───────────────────────────────────────┘
 *           ┌─────────────────────────┐
 *           │ Juan Alvaro Montoya     │
 *           │ jmontoya@servilink.com.co │
 *           ├─────────────────────────┤
 *           │ ◷  Mi cuenta            │
 *           ├─────────────────────────┤
 *           │ ⏻   Cerrar sesión       │
 *           └─────────────────────────┘
 */

import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { LogOut, User as UserIcon, ChevronDown, ShieldCheck } from 'lucide-react'

import { useCurrentUser } from '@/hooks/api/use-current-user'
import { useLogout } from '@/hooks/use-logout'
import { ROUTES } from '@/lib/constants'

export function UserMenu() {
  const { data: me, isLoading } = useCurrentUser()
  const logout = useLogout()
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (isLoading || !me) {
    return (
      <div className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-fg-muted">
        <div className="h-8 w-8 animate-pulse rounded-md bg-bg-elevated" />
        <div className="hidden sm:block">
          <div className="h-3 w-24 animate-pulse rounded bg-bg-elevated" />
          <div className="mt-1 h-2.5 w-16 animate-pulse rounded bg-bg-elevated" />
        </div>
      </div>
    )
  }

  const orgLabel =
    me.active_org?.nombre_comercial ?? me.active_org?.razon_social ?? null
  const roleLabel = me.active_org?.role ?? (me.memberships_count === 0 ? 'Persona natural' : null)

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-md p-1.5 text-sm transition-colors hover:bg-bg-elevated focus:bg-bg-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
      >
        <div
          aria-hidden
          className="flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br from-brand-500 to-rose-500 font-mono text-xs font-bold text-white shadow-sm"
        >
          {me.initials}
        </div>
        <div className="hidden text-left sm:block">
          <p className="text-sm font-medium leading-tight text-fg-primary">
            {me.full_name}
          </p>
          {(roleLabel || orgLabel) && (
            <p className="text-xs leading-tight text-fg-muted">
              {roleLabel}
              {roleLabel && orgLabel ? ' · ' : ''}
              {orgLabel}
            </p>
          )}
        </div>
        <ChevronDown
          className={`hidden h-4 w-4 text-fg-muted transition-transform sm:block ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-72 overflow-hidden rounded-xl border border-border bg-bg-surface py-1 shadow-xl"
        >
          {/* header — name, email, verification */}
          <div className="border-b border-border px-4 py-3">
            <p className="truncate text-sm font-semibold text-fg-primary">
              {me.full_name}
            </p>
            <p className="truncate text-xs text-fg-secondary">{me.email}</p>
            {orgLabel && (
              <p className="mt-2 truncate text-xs text-fg-muted">
                <span className="font-medium text-fg-secondary">
                  {me.active_org?.role ?? '—'}
                </span>
                <span className="px-1">·</span>
                {orgLabel}
                {me.active_org?.tax_id && (
                  <>
                    <span className="px-1">·</span>
                    NIT {me.active_org.tax_id}
                  </>
                )}
              </p>
            )}
            {me.totp_enabled && (
              <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-success-tint px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-success-fg">
                <ShieldCheck className="h-3 w-3" />
                MFA activo
              </p>
            )}
          </div>

          {/* nav items */}
          <div className="py-1">
            <Link
              to={ROUTES.PROFILE}
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-2 text-sm text-fg-secondary hover:bg-bg-elevated hover:text-fg-primary"
            >
              <UserIcon className="h-4 w-4" />
              Mi cuenta
            </Link>
          </div>

          {/* logout */}
          <div className="border-t border-border py-1">
            <button
              type="button"
              role="menuitem"
              onClick={async () => {
                setOpen(false)
                await logout()
              }}
              className="flex w-full items-center gap-3 px-4 py-2 text-sm text-error-fg hover:bg-error-tint"
            >
              <LogOut className="h-4 w-4" />
              Cerrar sesión
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
